/**
 * Settings → Guided Learning (beta): the GL-7 STUDENT self-consent surface
 * (consent-surface spec qlearn-gl-cohort-consent-surface-spec-v1.md D5).
 *
 * TWO-GATE dark posture:
 *   1. FE flag off (gl-consent-audience.js) => renders NOTHING (byte-identical).
 *   2. Flag on but the SERVER surface does not answer (404 => bean absent, the
 *      dark BE) => renders NOTHING. "Never show a toggle that cannot work" (D5).
 * Only when the flag is on AND GET /gl/consent answers does the section mount.
 *
 * Grant/revoke discipline:
 *   - Grant echoes GL_CONSENT_COPY_VERSION (the server rejects a stale version).
 *   - Revoke is one toggle + one confirm; the server stamps membership AND erases
 *     the derived GL cards in the same request (D3) — the confirm says so.
 *   - After ANY grant/revoke: glStatusReset() clears the session /gl/status latch
 *     then we re-probe our own status, so GL surfaces appear/disappear in-session
 *     without a reload (the status latch caches confirmed answers per session).
 *
 * Copy is the D6 PLACEHOLDER set — owner-ratified copy (new version id, both
 * repos) swaps in before any customer-visible flip.
 */
import { useCallback, useEffect, useState } from "react";
import { Box, Button, Divider, FormControlLabel, Switch, Typography } from "@material-ui/core";
import { glConsentAvailable } from "./gl-consent-audience";
import { glConsentCopy, GL_CONSENT_COPY_VERSION } from "./gl-consent-copy";
import { getGlConsent, grantGlConsent, revokeGlConsent } from "./gl-consent-api";
import { glStatusReset } from "../gl-shared/gl-status";

export default function GlConsentSection() {
  const enabled = glConsentAvailable();
  const [serverAnswered, setServerAnswered] = useState(null); // null=loading | true | false
  const [member, setMember] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const res = await getGlConsent();
    if (res.ok) {
      setServerAnswered(true);
      setMember(!!(res.data && res.data.member));
    } else {
      // 404 (server dark) / any error => the surface cannot work => stay hidden (D5).
      setServerAnswered(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      refresh();
    }
  }, [enabled, refresh]);

  // Gate 1 (flag) and Gate 2 (server answered): render nothing until BOTH clear.
  if (!enabled || serverAnswered !== true) {
    return null;
  }

  // After a write: clear the session status latch, then re-probe, so GL surfaces
  // flip in-session (D5). glStatusReset() forces every GL surface's next mount to
  // re-ask GET /gl/status instead of reusing the cached answer.
  const afterWrite = async () => {
    glStatusReset();
    await refresh();
  };

  const onToggle = async (event) => {
    if (event.target.checked) {
      setBusy(true);
      const res = await grantGlConsent(GL_CONSENT_COPY_VERSION);
      setError(!res.ok);
      await afterWrite();
      setBusy(false);
    } else {
      // D3: revoke deletes the derived cards — one confirm click suffices.
      if (!window.confirm(glConsentCopy.revokeConfirm)) {
        return;
      }
      setBusy(true);
      const res = await revokeGlConsent();
      setError(!res.ok);
      await afterWrite();
      setBusy(false);
    }
  };

  return (
    <Box data-testid="gl-consent-section" mt={3}>
      <Divider />
      <Box mt={2}>
        <Typography variant="h6">{glConsentCopy.title}</Typography>
        <Typography variant="body2" color="textSecondary">
          {glConsentCopy.body}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={member}
              onChange={onToggle}
              disabled={busy}
              inputProps={{ "data-testid": "gl-consent-toggle" }}
            />
          }
          label={glConsentCopy.title}
        />
      </Box>
      {error && (
        <Typography variant="body2" color="error" data-testid="gl-consent-error">
          {glConsentCopy.error}
        </Typography>
      )}
    </Box>
  );
}
